import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Avatar, Drawer, message, Tag } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { fetchAllActiveSessions, clientLogout } from "./event-management/EventApis";
import socket from "../services/socket";
import DefaultClientProfile from "../assets/user.jpg";

const ActiveUserTimers = () => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();

  const calculateTimeLeft = (loginTime) => {
    if (!loginTime) return 3600;
    const elapsedTime = dayjs().diff(dayjs(loginTime), "seconds");
    return Math.max(3600 - elapsedTime, 0);
  };

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds <= 0) {
      return "00:00";
    }
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const fetchActiveSessions = useCallback(async () => {
    try {
      const response = await fetchAllActiveSessions();
      if (response && response.success && Array.isArray(response.data)) {
        const sessionsWithTime = response.data.map((session) => ({
          ...session,
          remainingTime: calculateTimeLeft(session.loginTime),
        }));
        setActiveSessions(sessionsWithTime);
      } else {
        setActiveSessions([]);
      }
    } catch (error) {
      console.error("Error fetching active sessions in ActiveUserTimers:", error);
      setActiveSessions([]);
    }
  }, []);

  useEffect(() => {
    fetchActiveSessions();

    if (socket) {
      socket.on("userLoggedIn", fetchActiveSessions);
      socket.on("userLoggedOut", fetchActiveSessions);
      socket.on("user_logged_out", fetchActiveSessions);
      socket.on("allUsersLoggedOut", fetchActiveSessions);
    }

    return () => {
      if (socket) {
        socket.off("userLoggedIn", fetchActiveSessions);
        socket.off("userLoggedOut", fetchActiveSessions);
        socket.off("user_logged_out", fetchActiveSessions);
        socket.off("allUsersLoggedOut", fetchActiveSessions);
      }
    };
  }, [fetchActiveSessions]);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setActiveSessions((prevSessions) =>
        prevSessions.map((session) => ({
          ...session,
          remainingTime: calculateTimeLeft(session.loginTime),
        }))
      );
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, []);

  const handleIconClick = (session) => {
    setSelectedSession(session);
    setDrawerVisible(true);
  };

  const handleLogout = async () => {
    try {
      const userId =
        selectedSession?.userId?._id ||
        (typeof selectedSession?.userId === "string" ? selectedSession?.userId : null);
      const sessionId = selectedSession?._id;

      if (!userId) {
        throw new Error("Client ID is not available. Cannot log out.");
      }
      const data = await clientLogout(userId, sessionId);

      if (data && (data?.status === 200 || data?.success)) {
        message.success(data.message || "Client successfully logged out");
        setActiveSessions((prev) => prev.filter((s) => s._id !== sessionId));
        setDrawerVisible(false);
        setSelectedSession(null);
        fetchActiveSessions();
      } else {
        message.error(data?.message || "Logout failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during logout:", error);
      message.error("An error occurred during logout.");
    }
  };

  const publicRoutes = [
    "/",
    "/forgot-password",
    "/reset-password",
    "/verify-otp",
    "/reset-password-successfull",
  ];

  if (publicRoutes.includes(location.pathname)) return null;
  if (!activeSessions || activeSessions.length === 0) return null;

  const currentLiveSession =
    activeSessions.find((s) => s._id === selectedSession?._id) || selectedSession;
  const isLiveExpired = (currentLiveSession?.remainingTime || 0) <= 0;

  const loginTime = currentLiveSession?.loginTime;
  const formattedDate = loginTime ? moment(loginTime).format("DD-MM-YYYY") : "---";
  const formattedTime = loginTime ? moment(loginTime).format("HH:mm:ss") : "---";
  const avatarUrl =
    currentLiveSession?.personPhoto ||
    currentLiveSession?.userId?.clientPic ||
    currentLiveSession?.userId?.file ||
    DefaultClientProfile;

  const displayDob = (() => {
    const rawDob =
      currentLiveSession?.personDob ||
      currentLiveSession?.userId?.dob ||
      currentLiveSession?.dob;
    if (!rawDob) return "---";
    if (typeof rawDob === "string" && /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(rawDob)) {
      return rawDob;
    }
    const parsed = moment(rawDob);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(rawDob);
  })();

  return (
    <>
      <div
        className="active-timers-container"
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          zIndex: 990,
          pointerEvents: drawerVisible ? "none" : "auto",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "6px 8px",
        }}
      >
        {activeSessions.map((session) => {
          const isExpired = (session.remainingTime || 0) <= 0;
          const displayName =
            session.name ||
            `${session.userId?.foreName || ""} ${session.userId?.surName || ""}`.trim() ||
            "Client";

          return (
            <div
              key={session._id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() => handleIconClick(session)}
              title={`${displayName} - Click for details`}
            >
              <div
                style={{
                  width: "68px",
                  height: "68px",
                  backgroundColor: "#dc3545",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(220, 53, 69, 0.45)",
                  color: "#ffffff",
                  fontWeight: "bold",
                  fontSize: "11.5px",
                  padding: "4px",
                  lineHeight: "1.15",
                  border: "2.5px solid #ffffff",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  userSelect: "none",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.06)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(220, 53, 69, 0.65)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.45)";
                }}
              >
                {displayName}
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: "11px",
                  fontWeight: "bold",
                  color: "#ffffff",
                  minWidth: "54px",
                  padding: "2px 6px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "20px",
                  borderRadius: "10px",
                  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.25)",
                  backgroundColor: isExpired ? "#d9534f" : "#28a745",
                  letterSpacing: "0.5px",
                }}
              >
                <span>{formatTime(session.remainingTime)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <Drawer
        title={<span className="drawer-title fw-bold" style={{ fontSize: "1.25rem", margin: 0 }}>Active Client Vault Details</span>}
        placement="right"
        closable
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={420}
        zIndex={10000}
        mask={true}
        maskClosable={true}
        className="active-vault-details-drawer"
        styles={{
          mask: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          },
          content: {
            backgroundColor: "#ffffff",
            opacity: 1,
            boxShadow: "-10px 0 35px rgba(0, 0, 0, 0.45)",
          },
          header: {
            backgroundColor: "#ffffff",
            opacity: 1,
            borderBottom: "1px solid #e2e8f0",
            padding: "16px 20px",
          },
          body: {
            padding: 0,
            backgroundColor: "#ffffff",
            opacity: 1,
          },
        }}
        maskStyle={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        bodyStyle={{ padding: 0, backgroundColor: "#ffffff", opacity: 1 }}
      >
        {selectedSession ? (
          <div className="user-details-wrapper" style={{ backgroundColor: "#ffffff", opacity: 1 }}>
            <div className="user-header text-center p-4">
              <Avatar
                size={95}
                src={avatarUrl}
                alt={currentLiveSession?.name || "Client"}
                className="user-avatar mx-auto mb-2"
              />
              <h4 className="user-name mb-1">
                {currentLiveSession?.name ||
                  `${currentLiveSession?.userId?.foreName || ""} ${currentLiveSession?.userId?.surName || ""}`.trim() ||
                  "---"}
              </h4>
              <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap mt-2">
                {currentLiveSession?.personRole && (
                  <Tag color="cyan" style={{ fontWeight: 600 }}>
                    {currentLiveSession.personRole}
                  </Tag>
                )}
                <Tag
                  color={isLiveExpired ? "red" : "green"}
                  style={{ fontWeight: 700, padding: "2px 10px" }}
                >
                  {isLiveExpired ? "Time Over (00:00)" : `Time Left: ${formatTime(currentLiveSession.remainingTime)}`}
                </Tag>
              </div>
            </div>

            <div className="user-info p-4">
              <div className="info-item d-flex justify-content-between align-items-center mb-3">
                <strong>UCC Number:</strong>
                <span>{currentLiveSession?.userId?.uccNumber || currentLiveSession?.uccNumber || "---"}</span>
              </div>
              <div className="info-item d-flex justify-content-between align-items-center mb-3">
                <strong>Box Number:</strong>
                <span>{currentLiveSession?.userId?.boxNumber || currentLiveSession?.boxNumber || "---"}</span>
              </div>
              <div className="info-item d-flex justify-content-between align-items-center mb-3">
                <strong>Login Date:</strong>
                <span>{formattedDate}</span>
              </div>
              <div className="info-item d-flex justify-content-between align-items-center mb-3">
                <strong>Login Time:</strong>
                <span>{formattedTime}</span>
              </div>
              <div className="info-item d-flex justify-content-between align-items-center mb-3">
                <strong>Email:</strong>
                <span>{currentLiveSession?.userId?.email || currentLiveSession?.email || "---"}</span>
              </div>
              <div className="info-item d-flex justify-content-between align-items-center">
                <strong>DOB:</strong>
                <span>{displayDob}</span>
              </div>
            </div>
            <div className="user-footer text-center p-3 bg-light">
              <button
                className="btn btn-danger logout-button fw-bold px-4 py-2"
                onClick={handleLogout}
              >
                Log Out
              </button>
            </div>
          </div>
        ) : (
          <div className="no-user-selected text-center p-4">
            <p className="text-muted">No active session selected.</p>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default ActiveUserTimers;
